/**
 * SpaceSelector — stylized dropdown for the results visualizer.
 *
 * Design constraints:
 *  - Button width is fixed at mount time = widest option label + padding,
 *    measured via offscreen canvas.measureText (no DOM reflow).
 *  - Menu is absolutely positioned so opening cannot shift other elements.
 *  - Text-centered button, group headers, outside-click + Escape to close.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { SPACE_GROUP_LABEL_KEYS, type SpaceOption, type SpaceGroup } from './resultsSpaceOptions';
import './SpaceSelector.css';

interface SpaceSelectorProps {
  options: SpaceOption[];
  value: string;
  onChange: (id: string) => void;
}

function measureLongestLabel(labels: string[], font: string): number {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = font;
  let max = 0;
  for (const label of labels) {
    const w = ctx.measureText(label).width;
    if (w > max) max = w;
  }
  return max;
}

export const SpaceSelector: React.FC<SpaceSelectorProps> = ({ options, value, onChange }) => {
  const { t, i18n } = useTranslation('results');
  const [open, setOpen] = useState(false);
  const [buttonWidth, setButtonWidth] = useState<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const selected = options.find(o => o.id === value) ?? options[0];

  // Group options for rendering (preserve input order within each group).
  const grouped = useMemo(() => {
    const map = new Map<SpaceGroup, SpaceOption[]>();
    for (const opt of options) {
      const list = map.get(opt.group) ?? [];
      list.push(opt);
      map.set(opt.group, list);
    }
    return Array.from(map.entries());
  }, [options]);

  // Measure the widest label and fix the button width.
  //
  // Depends on the active language, not just on `options`: the button is sized
  // to the longest name, and French runs materially longer than English
  // ("Double Garage 1" → "Garage double 1"). Measuring once at mount would
  // leave the button sized for whichever language happened to be active then,
  // clipping the label after a switch.
  useLayoutEffect(() => {
    if (!buttonRef.current) return;
    const computed = window.getComputedStyle(buttonRef.current);
    const font = `${computed.fontStyle} ${computed.fontVariant} ${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}`;
    const widest = measureLongestLabel(options.map(o => t(o.labelKey)), font);
    // Add room for chevron icon + horizontal padding baked into the CSS.
    setButtonWidth(Math.ceil(widest) + 48);
  }, [options, t, i18n.language]);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!buttonRef.current || !menuRef.current) return;
      const target = e.target as Node;
      if (!buttonRef.current.contains(target) && !menuRef.current.contains(target)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSelect = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div className="space-selector position-relative">
      <button
        ref={buttonRef}
        type="button"
        className="space-selector-button"
        style={buttonWidth ? { width: buttonWidth } : undefined}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="space-selector-label">{t(selected.labelKey)}</span>
        <ChevronDown size={16} className="space-selector-chevron" />
      </button>

      {open && (
        <ul
          ref={menuRef}
          className="space-selector-menu"
          role="listbox"
        >
          {grouped.map(([group, items]) => (
            <React.Fragment key={group}>
              <li className="space-selector-group-header">{t(SPACE_GROUP_LABEL_KEYS[group])}</li>
              {items.map(opt => (
                <li
                  key={opt.id}
                  role="option"
                  aria-selected={opt.id === value}
                  className={`space-selector-option ${opt.id === value ? 'active' : ''}`}
                  onClick={() => handleSelect(opt.id)}
                >
                  {t(opt.labelKey)}
                </li>
              ))}
            </React.Fragment>
          ))}
        </ul>
      )}
    </div>
  );
};
